import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Infinity as InfinityIcon, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startCashfreePurchase } from "@/lib/cashfree";

const HIGHLIGHT_ID = "premium";

export default function PricingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => setPackages(data));
  }, []);

  const handleBuyNow = async (pkg) => {
    if (!user) { nav("/login"); return; }
    setBusy(pkg.id);
    try {
      await startCashfreePurchase({ packageId: pkg.id });
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-center mb-16">
        <p className="label-eyebrow text-gray-500 mb-3">Contact packages</p>
        <h1 className="font-display text-5xl sm:text-6xl brand-forest mb-4">First 10 owner contacts are on us.</h1>
        <p className="text-gray-600 max-w-2xl mx-auto text-lg">
          Serious about your search? Upgrade to reveal more owner contacts — pay once, get instant access.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {packages.map((pkg) => {
          const isPro = pkg.id === "pro";
          const highlight = pkg.id === HIGHLIGHT_ID;
          return (
            <div
              key={pkg.id}
              data-testid={`pkg-${pkg.id}`}
              className={`relative rounded-3xl p-8 border transition-all ${
                highlight
                  ? "bg-[#7A1E2A] text-white border-[#7A1E2A] scale-[1.03] shadow-2xl"
                  : "bg-white border-brand-subtle hover-lift"
              }`}
            >
              {highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0EA5E9] text-white text-[11px] font-bold tracking-widest uppercase px-4 py-1 rounded-full">
                  Most popular
                </span>
              )}
              <p className={`label-eyebrow mb-3 ${highlight ? "text-white/70" : "text-gray-500"}`}>{pkg.name}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-5xl font-bold">₹{pkg.amount}</span>
              </div>
              <p className={`text-sm mb-8 ${highlight ? "text-white/80" : "text-gray-500"}`}>{pkg.desc}</p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-sm">
                  {isPro ? <InfinityIcon size={16} className="brand-terracotta mt-0.5" /> : <Check size={16} className={`mt-0.5 ${highlight ? "text-white" : "brand-forest"}`} />}
                  <span>{isPro ? "Unlimited owner contacts" : `${pkg.credits} paid contact reveals`}</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check size={16} className={`mt-0.5 ${highlight ? "text-white" : "brand-forest"}`} />
                  <span>{isPro ? "Valid 30 days" : "Credits never expire"}</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check size={16} className={`mt-0.5 ${highlight ? "text-white" : "brand-forest"}`} />
                  <span>Direct owner phone & email</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check size={16} className={`mt-0.5 ${highlight ? "text-white" : "brand-forest"}`} />
                  <span>No brokerage. No middlemen.</span>
                </li>
              </ul>

              <Button
                data-testid={`buy-${pkg.id}`}
                onClick={() => handleBuyNow(pkg)}
                disabled={busy === pkg.id}
                className={`w-full rounded-full h-12 font-semibold flex items-center justify-center gap-2 ${
                  highlight
                    ? "bg-white text-[#7A1E2A] hover:bg-white/90"
                    : isPro
                    ? "bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
                    : "tlr-gold-btn"
                }`}
              >
                {busy === pkg.id ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {busy === pkg.id ? "Opening checkout…" : isPro ? "Go unlimited" : "Buy now"}
              </Button>
            </div>
          );
        })}
      </div>

      <p className="text-center mt-10 text-sm text-gray-500 flex items-center justify-center gap-2">
        <Sparkles size={14} className="brand-terracotta" /> Secure card, UPI &amp; netbanking payments via Cashfree.
      </p>
    </div>
  );
}
